import React, { useMemo } from 'react';
import { Card, Space, Typography } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { RouteViewModelExecution } from '../../../types/routeViewModel';
import { DataTable } from '../../../ui';
import { CONTROL_TASK_STATUS_LABELS } from '../../../constants/controlTaskStatuses';
import { TASK_REGISTRY } from '../../../constants/taskRegistry';

const { Text } = Typography;

export interface ExecutionBlockProps {
  model: RouteViewModelExecution;
}

const ExecutionBlock: React.FC<ExecutionBlockProps> = ({ model }) => {
  const ci = model.controlIssues;
  const issueCount =
    (ci?.critical?.length ?? 0) + (ci?.warnings?.length ?? 0) + (ci?.info?.length ?? 0);
  const hasContent = model.tasks.length > 0 || issueCount > 0;

  const overdueCount = useMemo(() => {
    return model.tasks.filter((t) => String(t.status).toUpperCase() === 'OVERDUE').length;
  }, [model.tasks]);

  const warningsCount = ci?.warnings?.length ?? 0;

  return (
    <Card
      size="small"
      title={<Space><ExclamationCircleOutlined />Исполнение и контроль</Space>}
      style={{ marginBottom: 16 }}
    >
      <div style={{ marginBottom: 12, fontSize: 13 }}>
        <Text>Просрочено: {overdueCount}</Text>
        <Text style={{ marginLeft: 20 }}>Предупреждения: {warningsCount}</Text>
      </div>
      {model.tasks.length > 0 ? (
        <DataTable
          size="small"
          pagination={false}
          rowKey="id"
          dataSource={model.tasks}
          columns={[
            {
              title: 'Задача',
              key: 'taskType',
              render: (_: unknown, row: { taskType: string }) =>
                TASK_REGISTRY[row.taskType]?.label ?? '—'
            },
            {
              title: 'Статус',
              dataIndex: 'status',
              width: 120,
              render: (s: string) => CONTROL_TASK_STATUS_LABELS[s] ?? '—'
            },
            {
              title: 'Срок',
              dataIndex: 'dueDate',
              width: 120,
              render: (d: string | null) => (d ? dayjs(d).format('DD.MM.YYYY') : '—')
            },
            {
              title: 'Выполнено',
              dataIndex: 'completedAt',
              width: 140,
              render: (d: string | null) => (d ? dayjs(d).format('DD.MM.YYYY HH:mm') : '—')
            }
          ]}
        />
      ) : null}

      {ci && issueCount > 0 ? (
        <div style={{ marginTop: model.tasks.length > 0 ? 16 : 0 }}>
          <Text strong style={{ fontSize: 12 }}>Замечания</Text>
          <Space direction="vertical" style={{ width: '100%', marginTop: 8 }} size="small">
            {ci.critical?.length > 0 && (
              <div>
                <Text type="danger" strong>Критические</Text>
                <ul style={{ margin: '4px 0 0 0', paddingLeft: 20 }}>
                  {ci.critical.map((issue) => (
                    <li key={`${issue.code}-${issue.message}`}>
                      <Text>{issue.message}</Text>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {ci.warnings?.length > 0 && (
              <div>
                <Text style={{ color: '#fa8c16' }} strong>Предупреждения</Text>
                <ul style={{ margin: '4px 0 0 0', paddingLeft: 20 }}>
                  {ci.warnings.map((issue) => (
                    <li key={`${issue.code}-${issue.message}`}>
                      <Text>{issue.message}</Text>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {ci.info?.length > 0 && (
              <div>
                <Text strong style={{ color: '#1890ff' }}>Информация</Text>
                <ul style={{ margin: '4px 0 0 0', paddingLeft: 20 }}>
                  {ci.info.map((issue) => (
                    <li key={`${issue.code}-${issue.message}`}>
                      <Text>{issue.message}</Text>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Space>
        </div>
      ) : null}

      {!hasContent ? (
        <Text type="secondary" style={{ fontSize: 13 }}>—</Text>
      ) : null}
    </Card>
  );
};

export default ExecutionBlock;
